import { CopyIcon } from "@expert/common-ui/assets";
import { Button, CheckIcon, Paper, ScrollArea, Text } from "@mantine/core";
import { useClipboard } from "@mantine/hooks";
import cx from "clsx";
import { type FC, useEffect, useMemo, useRef } from "react";
import type { TranscriptContent } from "../api/evaluations/types";
import type { Behavior } from "../types";
import classes from "./EvaluationPage.module.css";
import { EvaluationResultsTitle } from "./EvaluationResultsTitle";

export interface EvaluationTranscriptProps {
    activeBehavior?: string;
    onClickHighlight: (behavior?: string) => void;
    scrollOffset: number;
    evaluationResults?: TranscriptContent;
}

export const EvaluationTranscript: FC<EvaluationTranscriptProps> = ({
    activeBehavior,
    onClickHighlight,
    scrollOffset,
    evaluationResults,
}) => {
    const clipboard = useClipboard({ timeout: 500 });
    const trainingSessionId = evaluationResults?.SessionID;
    const copyToClipboard = () => {
        clipboard.copy(trainingSessionId);
    };
    const scrollableRef = useRef<HTMLDivElement>(null);
    const behaviors: { line: number; behaviorType: string }[] = useMemo(
        () =>
            evaluationResults?.EvaluationDetails?.Body?.filter(
                (behavior): behavior is Behavior => !!behavior.behaviorLine?.length && !!behavior.behavior,
            ).map((behavior) => ({
                line: parseInt(behavior.behaviorLine[0]),
                behaviorType: behavior.behavior,
            })) ?? [],
        [evaluationResults],
    );

    useEffect(() => {
        if (scrollableRef && activeBehavior) {
            const behaviorElementId = activeBehavior.toLocaleLowerCase().split(" ").join("_");
            const element = document.getElementById(behaviorElementId);
            if (element) {
                scrollableRef.current?.scroll({
                    top: element.offsetTop - scrollOffset,
                    behavior: "smooth",
                });
            } else {
                console.error("Element not found");
            }
        }
    }, [activeBehavior, scrollOffset]);

    return (
        <Paper
            shadow="xl"
            w="100%"
            h="100%"
            bg="var(--mantine-color-dark-9)"
            bdrs="0.75rem"
            style={{ display: "flex", flexDirection: "column" }}
        >
            <div className={classes.stickyHeader}>
                <EvaluationResultsTitle resultsStatus={evaluationResults?.EvaluationDetails?.Results} />
                <div className={classes.sessionIdRight}>
                    <Button
                        size="sm"
                        fw="300"
                        variant="transparent"
                        m="xs"
                        c="var(--mantine-color-gray-5)"
                        maw="200px"
                        rightSection={
                            clipboard.copied ? (
                                <CheckIcon width={20} height={20} />
                            ) : (
                                <CopyIcon width={20} height={21} stroke="var(--mantine-color-dark-3)" />
                            )
                        }
                        onClick={copyToClipboard}
                    >
                        <span className={classes.sessionIdLabel}>Session ID: {trainingSessionId}</span>
                    </Button>
                </div>
            </div>
            <ScrollArea
                className={classes.scrollAreaViewport}
                offsetScrollbars
                scrollbarSize={10}
                type="hover"
                viewportRef={scrollableRef}
                px="1.5rem"
            >
                {!evaluationResults?.Transcriptions?.length ? (
                    <div>
                        <Text size="1rem" m="0" c="var(--mantine-color-gray-5)" fw="400" lh="150%" fs="italic">
                            No transcript available, please press the “OK, got it” button in the Challenge Evaluation
                            box to return to your available challenges.
                        </Text>
                    </div>
                ) : (
                    evaluationResults?.Transcriptions?.map((transcript, i) => {
                        const evaluationBehavior = behaviors.find((behavior) => behavior.line === i + 1);
                        return Object.entries(transcript).map(([key, value]) => {
                            const elementId = evaluationBehavior?.behaviorType.toLocaleLowerCase().split(" ").join("_");
                            return key === "Timestamp" ? null : (
                                <div
                                    key={`${key}-${transcript.Timestamp}`}
                                    className={classes.transcriptBody}
                                    id={elementId}
                                    onClick={() => {
                                        onClickHighlight(evaluationBehavior?.behaviorType);
                                    }}
                                >
                                    <Text size="1rem" m="0" c="var(--mantine-color-gray-0)" fw="700" lh="150%">
                                        {key}
                                    </Text>
                                    <Text
                                        size="1rem"
                                        m="0"
                                        c="var(--mantine-color-gray-5)"
                                        fw="400"
                                        lh="150%"
                                        className={cx({ [classes.callout]: !!evaluationBehavior })}
                                    >
                                        {value}
                                    </Text>
                                </div>
                            );
                        });
                    })
                )}
            </ScrollArea>
        </Paper>
    );
};

export default EvaluationTranscript;
